'use client';

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vs } from 'react-syntax-highlighter/dist/cjs/styles/prism';
// Note: 'github' style is only available in hljs, not in prism
import remarkGfm from 'remark-gfm';
import { CustomImage } from './components/CustomImage';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  justified?: boolean;
}

const preprocessMarkdown = (markdown: string): string => {
  if (!markdown) return '';
  // Convert custom image size syntax `(url =600x400)` to a URL fragment `(url#600x400)`
  // This makes it a valid URL that the markdown parser won't reject.
  const customImageRegex = /\!\[(.*?)\]\((.*?)\s*=\s*(.*?)\)/g;
  return markdown.replace(customImageRegex, '![$1]($2#$3)');
};

export function MarkdownRenderer({ content, className = '', justified = false }: MarkdownRendererProps) {
  const processedContent = preprocessMarkdown(content);
  return (
    <div className={`markdown-content ${className}`}>
      <div className="prose prose-lg prose-blue max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ node, ...props }) => <h1 className="text-3xl font-bold mt-6 mb-4" {...props} />,
            h2: ({ node, ...props }) => <h2 className="text-2xl font-bold mt-5 mb-3" {...props} />,
            h3: ({ node, ...props }) => <h3 className="text-xl font-bold mt-4 mb-2" {...props} />,
            p: ({ node, ...props }) => <p className={`my-3 ${justified ? 'text-justify md:text-left hyphens-auto md:hyphens-manual' : ''}`} {...props} />,
            ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-3" {...props} />,
            ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-3" {...props} />,
            li: ({ node, ...props }) => <li className="my-1" {...props} />,
            // @ts-ignore - Custom image component that handles size syntax
            img: ({ node, ...props }) => <CustomImage {...props} />,
            // Table component overrides with inline styles for reliable rendering
            table: ({ node, ...props }) => (
              <div style={{ overflowX: 'auto', margin: '1.5rem 0' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.95em' }} {...props} />
              </div>
            ),
            thead: ({ node, ...props }) => (
              <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }} {...props} />
            ),
            // @ts-ignore
            tbody: ({ node, ...props }) => <tbody {...props} />,
            tr: ({ node, ...props }) => (
              <tr style={{ borderBottom: '1px solid #e2e8f0' }} {...props} />
            ),
            // @ts-ignore
            th: ({ node, ...props }) => (
              <th style={{ border: '1px solid #e2e8f0', padding: '10px 14px', fontWeight: 600, textAlign: 'left', backgroundColor: '#f8fafc' }} {...props} />
            ),
            // @ts-ignore
            td: ({ node, ...props }) => (
              <td style={{ border: '1px solid #e2e8f0', padding: '10px 14px' }} {...props} />
            ),
            // @ts-ignore - Ignoring type issues with react-markdown components
            code: ({ node, inline, className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || '');
              return !inline && match ? (
                <SyntaxHighlighter
                  style={vs}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            }
          }}
        >
          {processedContent || ''}
        </ReactMarkdown>
      </div>
      <style dangerouslySetInnerHTML={{
        __html: `
        .markdown-content .prose {
          width: 100%;
        }
        .markdown-content .prose pre {
          background-color: #f6f8fa;
          border-radius: 6px;
          padding: 16px;
          overflow: auto;
        }
        .markdown-content .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 6px;
        }
        .markdown-content .prose blockquote {
          border-left: 4px solid #ddd;
          padding-left: 1rem;
          color: #666;
          font-style: italic;
        }
        .markdown-content .prose table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
        }
        .markdown-content .prose table th,
        .markdown-content .prose table td {
          border: 1px solid #ddd;
          padding: 8px 12px;
        }
        .markdown-content .prose table th {
          background-color: #f6f8fa;
          font-weight: 600;
        }
        .markdown-content .prose table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
      ` }} />
    </div>
  );
}

export default MarkdownRenderer;
